import {
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  ToggleButtonGroupProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import { DayOfWeek } from "@pulse/pulse-rpcs/lib/email-cycle/dayOfWeek";
import { useBorder } from "@pulse/shared-components";
import {
  useButtonColorTokens,
  useCornerRadius,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";

interface DaysInputFieldProps extends Omit<ToggleButtonGroupProps, "color"> {
  label: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  readOnly?: boolean;
}

export const DaysInputField: React.FC<DaysInputFieldProps> = observer(
  ({
    label,
    error = false,
    helperText,
    required = false,
    readOnly = false,
    ...props
  }) => {
    const { t } = useTranslation();
    const spacing = useSpacing();
    const cornerRadius = useCornerRadius();
    const border = useBorder();
    const typography = useTypography();
    const tokens = useFoundationColorTokens();
    const buttonColorTokens = useButtonColorTokens();

    const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
      gap: spacing.spaceXS,
      // Each toggle button style
      [`& .${toggleButtonGroupClasses.firstButton}, & .${toggleButtonGroupClasses.middleButton}, & .${toggleButtonGroupClasses.lastButton}`]:
        {
          borderRadius: cornerRadius.radiusXS,
          borderLeft: border.buttonBorderOutlinedNeutral,
          paddingInline: spacing.spaceLG,
          backgroundColor: readOnly ? tokens.backgroundPageSubtle : undefined,
        },
      // Selected toggle button style
      [`& .${toggleButtonGroupClasses.selected}`]: {
        backgroundColor: `${tokens.backgroundPrimary} !important`,
        color: `${tokens.labelOnSurface} !important`,
      },
      // Selected toggle button style on hover
      [`& .${toggleButtonGroupClasses.selected}:hover`]: {
        backgroundColor: `${buttonColorTokens.buttonBackgroundFilledPrimaryHovered} !important`,
      },
      ...(readOnly && {
        pointerEvents: "none",
      }),
    }));

    const labelAndHelperTextColor = error ? tokens.labelError : tokens.label;
    const labelProps: TypographyProps = {
      ...typography.s2,
      color: labelAndHelperTextColor,
    };

    const getDayString = (day: DayOfWeek.DayOfWeek): string => {
      switch (day) {
        case DayOfWeek.DayOfWeek.SUN:
          return t("emailCycles.sunday");
        case DayOfWeek.DayOfWeek.MON:
          return t("emailCycles.monday");
        case DayOfWeek.DayOfWeek.TUE:
          return t("emailCycles.tuesday");
        case DayOfWeek.DayOfWeek.WED:
          return t("emailCycles.wednesday");
        case DayOfWeek.DayOfWeek.THU:
          return t("emailCycles.thursday");
        case DayOfWeek.DayOfWeek.FRI:
          return t("emailCycles.friday");
        case DayOfWeek.DayOfWeek.SAT:
          return t("emailCycles.saturday");
        default:
          return day satisfies never;
      }
    };

    return (
      <Stack gap={spacing.spaceXXS}>
        {/* Label */}
        <Stack direction="row" gap={spacing.spaceXXS}>
          <Typography {...labelProps}>{label}</Typography>
          {required && !readOnly && <Typography {...labelProps}>*</Typography>}
        </Stack>

        {/* Toggle buttons (Days) */}
        <StyledToggleButtonGroup {...props}>
          {Object.values(DayOfWeek.DayOfWeek).map((day) => (
            <ToggleButton key={day} value={day}>
              <Typography textTransform="capitalize" {...typography.bt1}>
                {getDayString(day)}
              </Typography>
            </ToggleButton>
          ))}
        </StyledToggleButtonGroup>

        {/* Helper Text */}
        {helperText && (
          <Typography color={labelAndHelperTextColor} {...typography.b3}>
            {helperText}
          </Typography>
        )}
      </Stack>
    );
  },
);
